import styled from 'styled-components'
import { EmptyData, Modal } from '@intelie/ui'

export const TemplatesContainer = styled.div<{ height: number }>`
    width: 1160px;
    height: ${({ height }) => height}px;
    margin: 0 auto;

    @media (max-width: 1599px) {
        width: 978px;
    }
`

export const TemplateListContainer = styled.div`
    height: 100%;
    width: 100%;

    padding: 16px;

    .page-header {
        display: flex;
        align-items: center;
        gap: 8px;

        .new-template-button {
            height: 34px;
            white-space: nowrap;
        }
    }

    .templates-limit-info {
        margin-top: 16px;
    }

    .templates-manage {
        display: flex;
        align-items: baseline;
        justify-content: space-between;

        margin: 16px 0 4px;
        width: 100%;

        .results-number {
            font: 400 16px / 24px var(--font-regular);
        }

        .status-control {
            display: flex;
            align-items: center;

            .divider {
                height: 24px;
                width: 1px;
                background: var(--gray-400);
            }

            button {
                border-radius: 0;
                padding: 8px;
                text-transform: uppercase;
            }
        }
    }

    .templates-table {
        margin-bottom: 16px;

        table {
            width: 100%;
            background: var(--gray-100);
            border: 1px solid var(--gray-400);

            *:not(i) {
                font: 400 16px / 24px var(--font-regular);
            }

            th,
            td {
                padding: 8px;
            }

            thead {
                background: var(--gray-100);
                border-bottom: 1px solid var(--gray-400);
                height: 48px;

                th {
                    font: 600 16px / 24px var(--font-semibold);
                }

                .template-checkbox {
                    width: 48px;
                }

                .template-status {
                    width: 130px;
                }

                .template-alias {
                    max-width: 100%;
                }

                .template-color {
                    width: 64px;
                }

                .template-category {
                    width: 96px;
                }

                .template-author {
                    width: 96px;
                }

                .template-lastModified {
                    width: 180px;
                }

                .template-actions {
                    width: 50px;
                }
            }

            tbody {
                tr {
                    background: var(--gray-0);
                    border-bottom: 1px solid var(--gray-400);
                    height: 48px;
                    transition: 0.25s;

                    &:hover {
                        background: var(--blue-200);
                        transition: 0.25s;
                    }
                }

                .template-alias {
                    cursor: pointer;
                }

                .template-status {
                    i {
                        font-size: 11px;
                        margin-right: 4px;
                    }

                    i.enabled {
                        color: var(--green-700);
                    }

                    i.disabled {
                        color: var(--red-500);
                    }
                }
            }
        }
    }
`

export const TemplateActionsContainer = styled.div`
    position: relative;

    .actions-trigger {
        color: var(--gray-800);
        padding: 4px 13px;
    }
`

export const TemplateActionsDropdownContainer = styled.div`
    position: absolute;
    right: 0;
    top: 36px;
    z-index: 1;

    background: var(--gray-0);
    border: 1px solid var(--gray-400);
    border-radius: 4px;
    box-shadow: 0px 4px 6px -2px #0000000d 0px 10px 15px -3px #0000001a;
    padding: 4px;

    width: 131px;

    button {
        border-radius: 3px;
        color: var(--gray-800);
        padding: 8px 16px;
        text-align: left;
        transition: 0.12s;
        width: 120px;

        :hover {
            background: var(--gray-200);
            transition: 0.12s;

            span,
            i {
                color: var(--blue-700);
            }
        }
    }

    .duplicate-button {
        display: flex;
        align-items: center;

        border-radius: 3px;
        color: var(--gray-800);
        height: 40px;
        font: 600 13px / 16px var(--font-semibold);
        padding: 4px 16px;
        text-align: center;
        text-decoration: none;
        transition: 0.12s;

        &:hover {
            background: var(--gray-200);
            color: var(--blue-700);
            transition: 0.12s;
        }

        .fa-copy {
            margin-right: 8px;
        }
    }
`

export const ColorSquare = styled.div<{ color: string }>`
    width: 14px;
    height: 14px;
    background: ${({ color }) => color};
    border-radius: 3px;
`

export const ModalMessage = styled.div`
    font: 400 16px / 24px var(--font-regular);
    width: 330px;
`

export const ModalActions = styled.div`
    display: flex;
    justify-content: end;
    gap: 8px;

    margin-top: 32px;

    .delete-action {
        background: var(--red-500);

        &:disabled {
            opacity: 0.5;
        }

        &:not(:disabled) {
            &:hover {
                background: var(--red-700);
            }
        }
    }
`

export const FormContainer = styled.div`
    margin: 16px;
    min-height: 250px;

    * {
        text-align: left;
    }

    .Select,
    .Select-control,
    input,
    textarea {
        font-size: 13px;
    }

    .template-select {
        margin-bottom: 25px;

        .live-select {
            margin-bottom: 2px;
        }
    }
`

export const StyledSection = styled.div`
    position: sticky;
    bottom: 0px;
    width: 100%;
    padding: 8px;
    background-color: ${({ theme }) => theme?.['gray-0'] || '#FFFFFF'};
    margin-bottom: 0;
    border-top: 1px solid ${({ theme }) => theme?.['gray-400'] || '#C8CCD4'};

    .btn-primary {
        background-color: ${({ theme }) => theme?.['blue-500'] || '#37A3EF'};
        border-color: ${({ theme }) => theme?.['blue-500'] || '#37A3EF'};
        color: ${({ theme }) => theme?.['gray-0'] || '#FFFFFF'};

        :hover {
            background-color: ${({ theme }) => theme?.['blue-700']};
        }
    }
`

export const ViewAnnotation = styled.div<{ annotationColor: string }>`
    background: ${({ annotationColor }) => annotationColor};
    border: 1px solid var(--gray-400);
    border-radius: 4px;
    cursor: pointer;
    padding-left: 8px;
    text-align: left;

    &:not(:last-child) {
        margin-bottom: 8px;
    }

    .color-border {
        background: var(--gray-0);
        border-radius: 0 2px 2px 0;
        padding: 12px;
        transition: 0.12s;

        &:hover {
            background: var(--blue-200);
            transition: 0.12s;
        }

        * {
            font: 400 14px / 20px var(--font-regular);
        }

        h1 {
            font: 600 16px / 18px var(--font-semibold);
        }

        p {
            margin: 0;

            &.details-info {
                color: var(--blue-700);
            }
        }
    }
`

export const ViewAnnotationExpanded = styled.div`
    padding: 16px;

    * {
        text-align: left;
    }

    input,
    textarea {
        cursor: not-allowed;

        &:disabled {
            color: var(--gray-800);
        }
    }

    .live-annotation__actions {
        position: relative;
    }

    .assets-info {
        display: flex;
        align-items: center;
        gap: 16px;
        margin: 16px 0;

        > div {
            width: 100%;
        }
    }
`

export const EditorHeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;

    background-color: var(--gray-200);
    border-bottom: 1px solid var(--gray-400);
    height: 44px;

    padding: 6px 24px;

    .left-header-content {
        padding: 0;

        .area-header-title {
            padding-left: 20px;
            padding-right: 6px;
        }

        .non-editable-tip {
            font: 20px / 24px var(--font-regular);
            margin-top: 4px;
            white-space: nowrap;

            i {
                margin-left: 4px;
                color: var(--yellow-500);
            }
        }

        .template-popover {
            position: relative;
        }

        .template-info-button {
            height: 100%;
            padding: 4px 12px;

            span {
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 4px;
            }
        }
    }

    .right-header-content {
        display: flex;
        gap: 16px;

        font-size: 14px;
        padding: 0;

        label {
            padding: 0;
            padding-top: 3px;
        }

        span {
            display: flex;
            align-items: center;
            gap: 4px;
        }
    }
`

export const AnnotationActions = styled.div`
    display: flex;
    align-items: center;
    justify-content: end;
    gap: 8px;

    margin: 8px 0;

    button {
        padding: 8px;
        transition: 0.12s;

        &:hover {
            transition: 0.12s;
        }

        &.remove {
            color: var(--red-700);
        }

        i.icon {
            font-size: 14px;
            width: 14px;
        }
    }
`

export const ModalContent = styled(Modal.Content)`
    padding: 0;
`

export const BuilderCheckboxContainer = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 25px;
`

export const PreviewContainer = styled.div`
    padding: 5px 0;
    height: 100%;
    max-height: 413px;
    overflow-y: auto;
    width: 345px;
    border: 1px solid var(--gray-400);
    border-radius: 4px;

    &::-webkit-scrollbar {
        width: 8px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background-color: var(--gray-400);
        border-radius: 20px;
        border: 2px solid transparent;
    }

    &::-webkit-scrollbar-thumb:hover {
        background-color: var(--gray-500);
    }
`

export const AnnotationEmptyData = styled(EmptyData)`
    height: 250px;
    width: 200px;

    margin: 0 auto;
`

export const FieldInfo = styled.span`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;

    color: var(--gray-800);
    font: 12px / 16px var(--font-regular);
    text-align: left;

    i {
        color: var(--gray-700);
    }
`

export const PopoverContainer = styled.div`
    position: absolute;
    left: 58px;
    top: 0;
    z-index: 1;

    background: var(--gray-0);
    border: 1px solid var(--gray-400);
    border-radius: 4px;
    box-shadow: 0px 4px 6px -2px #0000000d 0px 10px 15px -3px #0000001a;
    padding: 4px;

    width: 255px;

    button {
        text-align: left;
        padding: 4px 12px;
        height: 32px;
        font: 14px / 16px var(--font-semibold);
        color: var(--gray-800);

        i {
            min-width: 15px;
        }
    }
`

export const TemplateInfoModalContent = styled(Modal.Content)`
    width: 400px;

    .name-color-section {
        display: flex;
        gap: 16px;
        align-items: center;

        .name-section {
            width: 100%;
        }
    }

    .required {
        &::after {
            content: '*';
            color: var(--red-500);
            padding-left: 2px;
        }
    }
`

export const EditorContainer = styled.div`
    //padding: 16px;

    .section-body {
        display: flex;
        align-items: center;
        justify-content: center;
        border: 0;
    }

    .form-builder-button {
        margin-bottom: 16px;
    }

    .required {
        &::after {
            content: '*';
            color: var(--red-500);
            padding-left: 2px;
        }
    }

    .form-footer {
        margin-top: 0;
    }
`
